import { useNavigate, useParams } from "react-router";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import endpoints from "../../../services/endpoints";
import { TableDetailsItem } from "../../../components/tables/tableFunctions";
import { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { setPreviousUrl } from "../../../store/slices/geolocationSlice";
import GridTable from "../../../components/GridTable/GridTable";
import { DynamicFilterConfig } from "../../../components/DynamicComponent/DynamicFilterPanel";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import CustomRadioButton from "../../../components/buttons/CustomRadioButton";
import { formatDateToDDMMYYYY } from "../../../components/commonfunctions/convertDate";

interface UserManagementProps {
  type: string;
  testingProp?: boolean;
}

interface ChildData {
  id: string;
  user_code: string;
  first_name: string;
  last_name: string;
  country?: string;
  province?: string;
  region?: string;
}

interface TransactionData {
  transaction_id: string;
  transaction_date: string;
  transaction_category: string;
  wallet_type: string;
  requested_by: string;
  requested_from: string;
  receiver_location: string;
  sender_location: string;
  payment_type: string;
  transaction_status: string;
  amount: number;
  payable_amount: number;
  flagged: boolean;
  source_channel: string;
}

interface EntireResponse {
  message: string;
  child_data: ChildData;
  data: TransactionData[];
  total_count: number;
  total_amount: number;
  total_payables: number;
  total_receivables: number;
  opening_balance: string;
  closing_balance: string;
}

interface RenderProps {
  entireResponse: EntireResponse;
  loading: boolean;
}

interface RenderTopBarProps extends RenderProps {
  setSearchParams: (updater: (prev: URLSearchParams) => URLSearchParams) => void;
}

const SEARCH_PLACEHOLDERS = [
  'transaction id',
  'user code',
  'user name'
] as const;

export default function ListOfChildTransactions({ type }: UserManagementProps) {
  const { childTransaction } = endpoints;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);

  const { userCode, userName } = useSelector((state: RootState) => state.activeList);
  const [searchPlaceholder, setSearchPlaceholder] = useState<string>('user name');
  const [selectedView, setSelectedView] = useState("All");

  const rotatePlaceholder = useCallback(() => {
    setIsTransitioning(true);

    setTimeout(() => {
      setSearchPlaceholder(prev => {
        const currentIndex = SEARCH_PLACEHOLDERS.indexOf(prev as typeof SEARCH_PLACEHOLDERS[number]);
        const nextIndex = (currentIndex + 1) % SEARCH_PLACEHOLDERS.length; // Loop cyclically
        return SEARCH_PLACEHOLDERS[nextIndex];
      });
      setIsTransitioning(false);
    }, 500);
  }, []);

  useEffect(() => {
    const interval = setInterval(rotatePlaceholder, 2500);
    return () => clearInterval(interval);
  }, [rotatePlaceholder]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const viewFromUrl = params.get("transaction_view") || "All";
    setSelectedView(viewFromUrl);
  }, [window.location.search]);
  const TableDetails: Record<string, TableDetailsItem> = {
    "Transaction ID": {
      id: "transaction_id",
      className: "min-w-[100px]",
    },
    "Transaction Date and Time": {
      id: "transaction_date",
      sort: true,
      sortKey: "created_at",
      custom: ["transaction_date"],
      className: "min-w-[240px]",
    },
    "Transaction Type": {
      id: "transaction_category",
      className: "min-w-[170px]",
    },
    "Wallet": {
      id: "wallet_type",
      className: "min-w-[100px]",
      custom: ["wallet_type"],
    },
    "Requested By": {
      id: "requested_by",
      className: "min-w-[300px]",
    },
    "Requested From": {
      id: "requested_from",
      className: "min-w-[300px]",
    },
    "Requested By Location": {
      id: "receiver_location",
      className: "min-w-[350px]",
    },
    "Requested From Location": {
      id: "sender_location",
      className: "min-w-[350px]",
    },
    "Payment Type": {
      id: "payment_type",
      className: "min-w-[80px]",
      custom: ["payment_type"],
    },
    "Transaction Status": {
      id: "transaction_status",
      className: "min-w-[80px]",
    },
    "Recharged Phone Number": {
      id: "phone_number",
      className: "min-w-[100px]",
    },
    "Amount": {
      id: "amount",
      className: "min-w-[100px]",
      custom: ["amount"],
    },
    "Payable Amount": {
      id: "payable_amount",
      className: "min-w-[100px]",
      custom: ["payable_amount"],
    },
    "Flagged": {
      id: "flagged",
      className: "min-w-[80px]",
      custom: ["flagged"],
    },
    "Source of Request": {
      id: "source_channel",
      className: "min-w-[80px]",
    },
    Actions: {
      id: "actions",
      className: "min-w-[200px]",
      actionTypes: ["view"],
    },
  };

  const staticText = {
    emptyScreenText: {
      heading1: `There are no transactions yet`,
      heading2: ``,
    },
  };

  // Dynamic filter config for transactions
  // Dynamic filter config for transactions
  const dynamicFilterConfig: DynamicFilterConfig = {
    "Transaction Date": {
      label: "Transaction Date",
      type: "date",
      required: false,
      searchKey: {
        start: "transaction_start_date",
        end: "transaction_end_date",
      },
    },
    "Transaction Type": {
      label: "Transaction Type",
      type: "checkbox",
      options: ["eTop Up", "Recharge", "Bundle Activation", "Reversal", "Fund Transfer"],
      required: false,
      searchKey: "transaction_category",
    },
    "Wallet Type": {
      label: "Wallet Type",
      type: "checkbox",
      options: ["eTop Up", "Digital Wallet"],
      required: false,
      searchKey: "wallet_type",
    },
    "Requested By": {
      label: "Requested By",
      type: "checkbox",
      options: [`${userName?.trim()} (${userCode})`, "Others"],
      required: false,
      searchKey: "requested_by",
      valueMapping: {
        [`${userName?.trim()} (${userCode})`]: "Self",
        "Others": "Others"
      },
      reverseMapping: {
        "Self": `${userName?.trim()} (${userCode})`,
        "Others": "Others"
      },
    },
    "Payment Status": {
      label: "Payment Status",
      type: "checkbox",
      options: ["Approved", "Pending", "Rejected"],
      required: false,
      searchKey: "transaction_category_status",
    },
    "Payment Type": {
      label: "Payment Type",
      type: "checkbox",
      options: ["Paid", "Advance"],
      required: false,
      searchKey: "payment_type",
    },
    "Transaction Status": {
      label: "Transaction Status",
      type: "checkbox",
      options: ["Success", "Failed"],
      required: false,
      searchKey: "transaction_status",
    },
  };

  const handleActions = (userId: string, key: string, text: any) => {
    if (key === "view") {
      dispatch(setPreviousUrl(location.pathname + location.search))
      if (type === "ADM") {
        navigate(`/admin-management/list-of-admins/view-admin/${id}/transactions/${text?.id}`);
      } else {
        navigate(`/user-management/${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management/view-${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}/${id}/transactions/${text?.id}`);
      }
    }
  };

  const renderExtraHeading = ({ entireResponse, loading }: RenderProps) => {
    const queryParams = new URLSearchParams(window.location.search);
    const transactionStartDate = queryParams.get("transaction_start_date");
    const transactionEndDate = queryParams.get("transaction_end_date");

    const name = entireResponse?.child_data?.first_name ? `${entireResponse.child_data.first_name} ${entireResponse.child_data.last_name ?? ""}`.trim() : "—";

  const userCode = entireResponse?.child_data?.user_code ?? "N/A";
const address = [
    entireResponse?.child_data?.country || '',
    entireResponse?.child_data?.province || '', 
    entireResponse?.child_data?.region || ''
  ].filter(Boolean).join(', ') || '—';   const openingBalance = parseFloat(entireResponse?.opening_balance ?? "0");
  const closingBalance = parseFloat(entireResponse?.closing_balance ?? "0");
  const currency = "AFA";

  const dateLabel =
    transactionStartDate && transactionEndDate
      ? `From ${formatDateToDDMMYYYY(transactionStartDate)} to ${formatDateToDDMMYYYY(transactionEndDate)}`
    : `As on ${formatDateToDDMMYYYY(new Date().toISOString())}`;
      

    return (
      <div className="flex items-start justify-between p-3 bg-white border-b border-gray-200 rounded-t-lg min-h-[90px]">
        {/* Left Section: Name & ID */}
        <div className="flex flex-col ml-3 space-y-1">
          {loading ? (
            <>
              <div className="bg-gray-200 rounded-sm w-40 h-5 animate-pulse" />
              <div className="bg-gray-200 rounded-sm w-28 h-5 animate-pulse" />
            </>
          ) : (
            <>
              <h2 className="text-heading2 text-primary_text">
                {name}
              </h2>
              <p className="text-subTitle text-primary_text">
                {userCode}
              </p>
              <p className="text-subTitle text-primary_text">
                {address}
              </p>
            </>
          )}
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-6 mr-3">
          {/* Date label */}
          <div>
            {loading ? (
              <div className="bg-gray-200 rounded-sm w-52 h-4 animate-pulse mb-7" />
            ) : (
              <p className="text-subTitle text-primary_text mb-7">
                {dateLabel}
              </p>
            )}
          </div>

          {/* Running Balance */}
          <div className="flex flex-col items-start space-y-1 min-w-[120px]">
            {loading ? (
              <>
                <div className="bg-gray-200 rounded-sm w-28 h-4 animate-pulse" />
                <div className="bg-gray-200 rounded-sm w-28 h-6 animate-pulse" />
              </>
            ) : (
              <>
                <p className="text-subTitle text-primary_text">Opening Balance</p>
                <p className="text-heading4 text-[#144EA2]">
                  {currency} {openingBalance.toFixed(2)}
                </p>
              </>
            )}
          </div>

          {/* Divider */}
          <div className="w-px h-12 bg-gray-300" />

          {/* Closing Balance */}
          <div className="flex flex-col items-start space-y-1 min-w-[120px]">
            {loading ? (
              <>
                <div className="bg-gray-200 rounded-sm w-28 h-4 animate-pulse" />
                <div className="bg-gray-200 rounded-sm w-28 h-6 animate-pulse" />
              </>
            ) : (
              <>
                <p className="text-subTitle text-primary_text mr-2">Closing Balance</p>
                <p className="text-heading4 text-[#144EA2]">
                  {currency} {closingBalance.toFixed(2)}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderTopBarExtras = ({
    entireResponse,
    setSearchParams,
    loading,
  }: RenderTopBarProps) => (
    <div className="mb-[-10px] ml-1.5">
      {loading ? (
        <div className="flex items-center gap-4 px-3 pt-2.5">
          <div className="h-8 w-20 animate-pulse bg-gray-200 rounded-sm"></div>
          <div className="h-8 w-80 animate-pulse bg-gray-200 rounded-sm"></div>
          <div className="h-8 w-80 animate-pulse bg-gray-200 rounded-sm"></div>
        </div>
      ) : (
        <CustomRadioButton
          options={[
            { id: "All", label: "All" },
            {
              id: "Total Payables",
              label: `Total Payables - AFA ${Number(
                entireResponse?.total_payables
              ).toFixed(2)}`,
            },
            {
              id: "Total Receivables",
              label: `Total Receivables - AFA ${Number(
                entireResponse?.total_receivables
              ).toFixed(2)}`,
            },
          ]}
          isDisabled={[
            entireResponse?.total_payables ? "" : "Total Payables",
            entireResponse?.total_receivables ? "" : "Total Receivables",
          ]}
          selected={selectedView}
          textClasses="text-subTitle"
          onChange={(val) => {
            setSelectedView(val);
            setSearchParams((prev: URLSearchParams) => {
              const updated = new URLSearchParams(prev);
              if (val === "All") {
                updated.delete("transaction_view");
              } else {
                updated.set("transaction_view", val);
              }
              // Reset to first page when switching views
              updated.set("page_number", "1");
              return updated;
            });
          }}
        />
      )}
    </div>
  );

  const renderSummary = ({ entireResponse, loading }: RenderProps) => (
    <div className="mt-3 flex justify-between pr-1 ">
      {loading ? (
        <>
          <div className="h-6 w-48 animate-pulse bg-gray-200 rounded-sm"></div>
          <div className="h-6 w-48 animate-pulse bg-gray-200 rounded-sm"></div>
        </>
      ) : (
        <>
          <div>
            No. of Transactions{" "}
            <span className="text-p_primary font-bold">{entireResponse?.total_count ?? 0}</span>
          </div>
          <div>
            Sum of Total{" "}
            <span className="text-p_primary font-bold">
              AFA {Number(entireResponse?.total_amount).toFixed(2) ?? 0}
            </span>
          </div>
        </>
      )}
    </div>
  );

  // Determine breadcrumb configuration based on type
  const getBreadcrumbConfig = () => {
    if (type === "ADM") {
      return {
        navPath: ["Admin Management", "List of Admins", "View Admin Profile", "View Admin Transactions"],
        heading: "List of Transactions",
        links: [
          "/admin-management/list-of-admins",
          "/admin-management/list-of-admins", 
          `/admin-management/list-of-admins/view-admin/${id}`,
          `/admin-management/list-of-admins/view-admin/${id}/transactions`
        ]
      };
    } else {
      return {
        navPath: ["User Management", `${type.slice(0, -1)} Management`, `List of ${type}`, `View ${type.slice(0, -1)} Profile`, `View ${type.slice(0, -1)} Transactions`],
        heading: "List of Transactions",
        links: [
          `/user-management/${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management`, 
          `/user-management/${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management`, 
          `/user-management/${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management`, 
          `/user-management/${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management/view-${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}/${id}`
        ]
      };
    }
  };

  const breadcrumbConfig = getBreadcrumbConfig();

  return (
    <div>
      <Breadcrumbs
        navPath={breadcrumbConfig.navPath}
        heading={breadcrumbConfig.heading}
        noNavigation
        links={breadcrumbConfig.links}
        list={false}
      />
      <GridTable
        staticText={staticText}
        mainEndPoint={`${childTransaction}/${id}`}
        TableDetails={TableDetails}
        handleActions={handleActions}
        params={{ 'page_number': '1', sort: "created_at+desc", user_type: type === 'ADM' ? 'ADM' : userCode?.toUpperCase().match(/^[A-Z]+/)?.[0] || type }}
        searchPlaceholder={`Search by ${searchPlaceholder}`}
        placeholderClassName={`search-placeholder ${isTransitioning ? 'transitioning' : ''}`}
        filters={true}
        serviceNow={type === 'Customers' ? false : true}
        exporting={true}
        reportType="Transactions"
        notfoundRoute={{ 
          state: { 
            route: type === "ADM" 
              ? "/admin-management/list-of-admins" 
              : `/user-management/${type.toLowerCase().replace(/ /g, '-').slice(0, -1)}-management` 
          } 
        }}
        title={`${type} Management`}
        dynamicFilterConfig={dynamicFilterConfig}
        renderTopBarExtras={renderTopBarExtras}
        renderSummary={renderSummary}
        renderExtraHeading={renderExtraHeading}
        addMorePadding={true}
        customEmptyScreenHeight="h-32 lg:h-60"
        emptyScreenHeight="h-[450px]"
        heightClassName="h-childtransactionListHeight"
        customHeight="h-[110px]"
        hasViewMDPrivilege={true}
        mainDivScroll={true}
        />
    </div>
  );
}